/**
 * Admin Audit Log — Actions (HTTP), Data changes (row audit), Impersonation.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { adminAuditAPI } from '../services/adminApi';

function defaultFromLocal() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 16);
}

function defaultToLocal() {
  return new Date().toISOString().slice(0, 16);
}

function toIso(localDatetime) {
  if (!localDatetime) return undefined;
  const d = new Date(localDatetime);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-CH', {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  } catch {
    return String(iso);
  }
}

function unwrap(res) {
  return res?.data?.data ?? res?.data ?? res;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  color: #111827;
`;

const Help = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.9rem;
  max-width: 52rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
`;

const TabBtn = styled.button`
  border: 1px solid ${(p) => (p.$active ? '#1f2937' : '#d1d5db')};
  background: ${(p) => (p.$active ? '#1f2937' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#374151')};
  padding: 0.45rem 0.85rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.65rem;
  align-items: end;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.85rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #4b5563;
`;

const Input = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  font-size: 0.875rem;
`;

const Select = styled.select`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  font-size: 0.875rem;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const Btn = styled.button`
  border: 1px solid #d1d5db;
  background: ${(p) => (p.$primary ? '#1f2937' : '#fff')};
  color: ${(p) => (p.$primary ? '#fff' : '#374151')};
  padding: 0.45rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.55rem 0.65rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.5rem 0.65rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
  color: #111827;
`;

const Meta = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
`;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.35);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
`;

const Drawer = styled.div`
  width: min(520px, 100%);
  height: 100%;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  padding: 1rem 1.1rem;
  overflow-y: auto;
`;

const Pre = styled.pre`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.65rem;
  font-size: 0.72rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const PAGE = 50;

export default function AdminAuditLogPanel() {
  const [tab, setTab] = useState('actions');
  const [fromLocal, setFromLocal] = useState(defaultFromLocal);
  const [toLocal, setToLocal] = useState(defaultToLocal);
  const [adminId, setAdminId] = useState('');
  const [method, setMethod] = useState('');
  const [q, setQ] = useState('');
  const [tableName, setTableName] = useState('');
  const [operation, setOperation] = useState('');
  const [offset, setOffset] = useState(0);
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [applied, setApplied] = useState(() => ({
    from: toIso(defaultFromLocal()),
    to: toIso(defaultToLocal()),
    adminId: '',
    method: '',
    q: '',
    tableName: '',
    operation: '',
  }));

  const filterKey = useMemo(
    () => ({ ...applied, offset, tab }),
    [applied, offset, tab]
  );

  const { data: adminsRes } = useQuery(['admin-audit-admins'], () => adminAuditAPI.listAdmins(), {
    staleTime: 60_000,
  });
  const admins = unwrap(adminsRes) || [];

  const { data: tablesRes } = useQuery(['admin-audit-tables'], () => adminAuditAPI.listTables(), {
    staleTime: 60_000,
    enabled: tab === 'rows',
  });
  const tables = unwrap(tablesRes) || [];

  const actionsQuery = useQuery(
    ['admin-audit-actions', filterKey],
    () =>
      adminAuditAPI.listActions({
        from: applied.from,
        to: applied.to,
        adminId: applied.adminId || undefined,
        method: applied.method || undefined,
        q: applied.q || undefined,
        limit: PAGE,
        offset,
      }),
    { enabled: tab === 'actions', keepPreviousData: true }
  );

  const rowsQuery = useQuery(
    ['admin-audit-rows', filterKey],
    () =>
      adminAuditAPI.listRows({
        from: applied.from,
        to: applied.to,
        table: applied.tableName || undefined,
        operation: applied.operation || undefined,
        q: applied.q || undefined,
        changedBy: applied.adminId || undefined,
        limit: PAGE,
        offset,
      }),
    { enabled: tab === 'rows', keepPreviousData: true }
  );

  const impersonationsQuery = useQuery(
    ['admin-audit-impersonations', filterKey],
    () =>
      adminAuditAPI.listImpersonations({
        from: applied.from,
        to: applied.to,
        adminId: applied.adminId || undefined,
        limit: PAGE,
        offset,
      }),
    { enabled: tab === 'impersonations', keepPreviousData: true }
  );

  const activeBundle =
    tab === 'actions'
      ? unwrap(actionsQuery.data)
      : tab === 'rows'
        ? unwrap(rowsQuery.data)
        : unwrap(impersonationsQuery.data);

  const rows = activeBundle?.rows || [];
  const total = activeBundle?.total ?? 0;
  const loading =
    (tab === 'actions' && actionsQuery.isLoading) ||
    (tab === 'rows' && rowsQuery.isLoading) ||
    (tab === 'impersonations' && impersonationsQuery.isLoading);

  const applyFilters = () => {
    setApplied({
      from: toIso(fromLocal),
      to: toIso(toLocal),
      adminId,
      method,
      q,
      tableName,
      operation,
    });
    setOffset(0);
  };

  const downloadExport = async () => {
    setExporting(true);
    try {
      const type =
        tab === 'actions' ? 'actions' : tab === 'rows' ? 'rows' : 'impersonations';
      const res = await adminAuditAPI.exportCsv({
        type,
        from: applied.from,
        to: applied.to,
        table: applied.tableName || undefined,
        limit: 5000,
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin-audit-${type}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      window.alert('Export failed. Try again or narrow the date range.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Wrap>
      <div>
        <Title>Audit log</Title>
        <Help>
          Concrete proof of admin activity: who did what, when, and on which data. Defaults to the last
          30 days. Entries are append-only. Historical gaps before this feature may lack attribution.
        </Help>
      </div>

      <Tabs>
        <TabBtn
          type="button"
          $active={tab === 'actions'}
          onClick={() => {
            setTab('actions');
            setOffset(0);
            setDetail(null);
          }}
        >
          Actions (API)
        </TabBtn>
        <TabBtn
          type="button"
          $active={tab === 'rows'}
          onClick={() => {
            setTab('rows');
            setOffset(0);
            setDetail(null);
          }}
        >
          Data changes
        </TabBtn>
        <TabBtn
          type="button"
          $active={tab === 'impersonations'}
          onClick={() => {
            setTab('impersonations');
            setOffset(0);
            setDetail(null);
          }}
        >
          View as player
        </TabBtn>
      </Tabs>

      <Filters>
        <Field>
          From
          <Input type="datetime-local" value={fromLocal} onChange={(e) => setFromLocal(e.target.value)} />
        </Field>
        <Field>
          To
          <Input type="datetime-local" value={toLocal} onChange={(e) => setToLocal(e.target.value)} />
        </Field>
        <Field>
          Admin
          <Select value={adminId} onChange={(e) => setAdminId(e.target.value)}>
            <option value="">All admins</option>
            {(Array.isArray(admins) ? admins : []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.email || a.name || a.id}
              </option>
            ))}
          </Select>
        </Field>
        {tab === 'actions' && (
          <Field>
            Method
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="">All</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </Select>
          </Field>
        )}
        {tab === 'rows' && (
          <>
            <Field>
              Table
              <Select value={tableName} onChange={(e) => setTableName(e.target.value)}>
                <option value="">All tables</option>
                {(Array.isArray(tables) ? tables : []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              Operation
              <Select value={operation} onChange={(e) => setOperation(e.target.value)}>
                <option value="">All</option>
                <option value="INSERT">INSERT</option>
                <option value="UPDATE">UPDATE</option>
                <option value="DELETE">DELETE</option>
              </Select>
            </Field>
          </>
        )}
        <Field>
          Search
          <Input
            type="search"
            placeholder={tab === 'actions' ? 'Path or email…' : 'Table, record, email…'}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </Field>
        <ActionsRow>
          <Btn type="button" $primary onClick={applyFilters}>
            Apply
          </Btn>
          <Btn type="button" onClick={downloadExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Btn>
        </ActionsRow>
      </Filters>

      <Meta>
        {loading ? 'Loading…' : `Showing ${rows.length} of ${total}`}
        {offset > 0 ? ` (offset ${offset})` : ''}
      </Meta>

      <TableWrap>
        <Table>
          <thead>
            {tab === 'actions' && (
              <tr>
                <Th>When</Th>
                <Th>Admin</Th>
                <Th>Method</Th>
                <Th>Path</Th>
                <Th>Status</Th>
                <Th>IP</Th>
                <Th />
              </tr>
            )}
            {tab === 'rows' && (
              <tr>
                <Th>When</Th>
                <Th>Admin</Th>
                <Th>Table</Th>
                <Th>Op</Th>
                <Th>Record</Th>
                <Th>changed_by</Th>
                <Th />
              </tr>
            )}
            {tab === 'impersonations' && (
              <tr>
                <Th>When</Th>
                <Th>Admin</Th>
                <Th>Player</Th>
                <Th>Expires</Th>
                <Th>IP</Th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <Td colSpan={7} style={{ color: '#6b7280' }}>
                  No entries for this filter.
                </Td>
              </tr>
            ) : null}
            {tab === 'actions' &&
              rows.map((r) => (
                <tr key={r.id}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatWhen(r.created_at)}</Td>
                  <Td>
                    <div>{r.admin_email || '—'}</div>
                    <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>{r.actor_label || ''}</div>
                  </Td>
                  <Td>{r.method}</Td>
                  <Td style={{ maxWidth: 280, wordBreak: 'break-all' }}>{r.path}</Td>
                  <Td style={{ color: r.success ? '#059669' : '#b91c1c' }}>{r.status_code}</Td>
                  <Td>{r.ip || '—'}</Td>
                  <Td>
                    <Btn type="button" onClick={() => setDetail({ kind: 'action', row: r })}>
                      Detail
                    </Btn>
                  </Td>
                </tr>
              ))}
            {tab === 'rows' &&
              rows.map((r) => (
                <tr key={r.id}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatWhen(r.changed_at)}</Td>
                  <Td>{r.admin_email || '—'}</Td>
                  <Td>{r.table_name}</Td>
                  <Td>{r.operation}</Td>
                  <Td style={{ fontSize: '0.72rem', maxWidth: 140, wordBreak: 'break-all' }}>
                    {r.record_id || '—'}
                  </Td>
                  <Td style={{ fontSize: '0.72rem' }}>{r.changed_by || '—'}</Td>
                  <Td>
                    <Btn type="button" onClick={() => setDetail({ kind: 'row', row: r })}>
                      Detail
                    </Btn>
                  </Td>
                </tr>
              ))}
            {tab === 'impersonations' &&
              rows.map((r) => (
                <tr key={r.id}>
                  <Td style={{ whiteSpace: 'nowrap' }}>{formatWhen(r.created_at)}</Td>
                  <Td>{r.admin_email || '—'}</Td>
                  <Td>
                    <div>{r.player_name || '—'}</div>
                    <div style={{ color: '#6b7280', fontSize: '0.72rem' }}>{r.player_email || ''}</div>
                  </Td>
                  <Td>{formatWhen(r.expires_at)}</Td>
                  <Td>{r.ip_address || '—'}</Td>
                </tr>
              ))}
          </tbody>
        </Table>
      </TableWrap>

      <ActionsRow>
        <Btn type="button" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE))}>
          Previous
        </Btn>
        <Btn
          type="button"
          disabled={offset + PAGE >= total}
          onClick={() => setOffset((o) => o + PAGE)}
        >
          Next
        </Btn>
      </ActionsRow>

      {detail && (
        <DrawerOverlay onClick={() => setDetail(null)}>
          <Drawer onClick={(e) => e.stopPropagation()}>
            <ActionsRow style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <strong>Entry detail</strong>
              <Btn type="button" onClick={() => setDetail(null)}>
                Close
              </Btn>
            </ActionsRow>
            {detail.kind === 'action' && (
              <>
                <Meta>
                  {formatWhen(detail.row.created_at)} · {detail.row.admin_email || 'unknown'} ·{' '}
                  {detail.row.method} {detail.row.path}
                </Meta>
                <h4 style={{ margin: '0.75rem 0 0.35rem' }}>Request summary</h4>
                <Pre>{JSON.stringify(detail.row.request_summary, null, 2)}</Pre>
                {detail.row.error_message ? (
                  <>
                    <h4 style={{ margin: '0.75rem 0 0.35rem' }}>Error</h4>
                    <Pre>{detail.row.error_message}</Pre>
                  </>
                ) : null}
                <h4 style={{ margin: '0.75rem 0 0.35rem' }}>User agent</h4>
                <Pre>{detail.row.user_agent || '—'}</Pre>
              </>
            )}
            {detail.kind === 'row' && (
              <>
                <Meta>
                  {formatWhen(detail.row.changed_at)} · {detail.row.table_name} · {detail.row.operation} ·{' '}
                  {detail.row.admin_email || detail.row.changed_by || 'system/unknown'}
                </Meta>
                <h4 style={{ margin: '0.75rem 0 0.35rem' }}>Before (old_data)</h4>
                <Pre>{JSON.stringify(detail.row.old_data, null, 2)}</Pre>
                <h4 style={{ margin: '0.75rem 0 0.35rem' }}>After (new_data)</h4>
                <Pre>{JSON.stringify(detail.row.new_data, null, 2)}</Pre>
              </>
            )}
          </Drawer>
        </DrawerOverlay>
      )}
    </Wrap>
  );
}
